import React, {KeyboardEvent, use} from "react";
import {
    HiddenVisually,
    ScrollArea,
    View
} from "reshaped";
import {RatingProfileId} from "../models/models";
import {useTranslation} from "react-i18next";
import {loadRatingProfiles} from "../utils/api_client";


interface RatingProfileSwitcherProps {
    ratingProfileId: RatingProfileId;
    onRatingProfileSelected: (ratingProfileId: RatingProfileId) => void;
}

export function RatingProfileSwitcher({ ratingProfileId, onRatingProfileSelected }: RatingProfileSwitcherProps) {
    const { t } = useTranslation();
    const ratingProfiles = use(loadRatingProfiles())
    const ratingProfilesWithNoProfile = [{ id: null, name: t("rating_profile_switcher.no_profile") }, ...ratingProfiles]

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        const currentButton = e.currentTarget;
        const parent = currentButton.parentElement;
        if (!parent) return;

        // Get all button siblings as an array
        const buttons = Array.from(parent.querySelectorAll<HTMLButtonElement>('button[role="radio"]'));
        const currentIndex = buttons.indexOf(currentButton);

        let newIndex = currentIndex;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            newIndex = (currentIndex + 1) % buttons.length;
            buttons[newIndex].focus();
            e.preventDefault();
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            newIndex = (currentIndex - 1 + buttons.length) % buttons.length;
            buttons[newIndex].focus();
            e.preventDefault();
        } else if (e.key === " " || e.key === "Enter") {
            onRatingProfileSelected(ratingProfilesWithNoProfile[currentIndex].id);
            e.preventDefault();
        }
    };
    return (
        <div className={"absolute max-w-full z-150"}>
            <h2 className="sr-only">{t("rating_profile_switcher.title")}</h2>
            <ScrollArea scrollbarDisplay={"hidden"}>
                <View direction={"row"} gap={2} padding={4} wrap={false} className={"l:flex-wrap!"} attributes={{ role: "radiogroup", "aria-label": t("rating_profile_switcher.label") }}>
                    {ratingProfilesWithNoProfile.map((rp) => {
                        const active = ratingProfileId === rp.id;
                        return (
                            <button
                                key={rp.id}
                                className={"px-2 py-1 cursor-pointer rounded-full font-medium! shadow-md " + (active ? "bg-primary text-on-primary" : "bg-page text-on-page")}
                                role="radio"
                                aria-checked={active}
                                tabIndex={active ? 0 : -1}
                                onClick={() => onRatingProfileSelected(rp.id)}
                                onKeyDown={handleKeyDown}>
                                {rp.name} {active && <HiddenVisually>{t("rating_profile_switcher.active")}</HiddenVisually>}
                            </button>
                        )})}
                </View>
            </ScrollArea>
        </div>
    );
}
